import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, GripVertical, ArrowUp, ArrowDown,
  Save, X, Menu, ExternalLink, LayoutGrid, AlertCircle
} from 'lucide-react';
import { supabase } from '../../supabase/client';

interface NavItem {
  id: number;
  name: string;
  path: string;
  icon?: string;
  sort_order: number;
  is_visible: boolean;
  is_custom: boolean;
  is_system: boolean;
  link_type: string;
  custom_page_id?: number;
  parent_id?: number;
  created_at: string;
  updated_at: string;
}

interface NavItemForm {
  name: string;
  path: string;
  icon: string;
  link_type: string;
  is_visible: boolean;
}

const linkTypeOptions = [
  { value: 'internal', label: '内部链接' },
  { value: 'external', label: '外部链接' },
  { value: 'custom_page', label: '自定义页面' },
];

const iconOptions = [
  { value: '', label: '无图标' },
  { value: 'Home', label: '首页' },
  { value: 'BookOpen', label: '书本' },
  { value: 'Download', label: '下载' },
  { value: 'ShoppingBag', label: '购物' },
  { value: 'User', label: '用户' },
  { value: 'Settings', label: '设置' },
  { value: 'HelpCircle', label: '帮助' },
  { value: 'Code2', label: '代码' },
  { value: 'Blocks', label: '积木' },
  { value: 'GraduationCap', label: '毕业帽' },
  { value: 'FileText', label: '文件' },
];

export default function NavItems() {
  const navigate = useNavigate();
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<NavItem | null>(null);
  const [form, setForm] = useState<NavItemForm>({
    name: '',
    path: '',
    icon: '',
    link_type: 'internal',
    is_visible: true,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadNavItems();
  }, []);

  const loadNavItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('nav_items')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setNavItems(data || []);
    } catch (err) {
      
      setError('加载导航项失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('请输入导航名称');
      return;
    }
    if (!form.path.trim()) {
      setError('请输入导航路径');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingItem) {
        const { error } = await supabase
          .from('nav_items')
          .update({
            name: form.name.trim(),
            path: form.path.trim(),
            icon: form.icon || null,
            link_type: form.link_type,
            is_visible: form.is_visible,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingItem.id);

        if (error) throw error;
      } else {
        const maxOrder = navItems.length > 0
          ? Math.max(...navItems.map(item => item.sort_order))
          : 0;

        const { error } = await supabase
          .from('nav_items')
          .insert({
            name: form.name.trim(),
            path: form.path.trim(),
            icon: form.icon || null,
            link_type: form.link_type,
            is_visible: form.is_visible,
            is_custom: true,
            is_system: false,
            sort_order: maxOrder + 1,
          });

        if (error) throw error;
      }

      await loadNavItems();
      closeModal();
    } catch (err) {
      
      setError('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这个导航项吗？')) return;

    try {
      const { error } = await supabase
        .from('nav_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadNavItems();
    } catch (err) {
      
      setError('删除失败');
    }
  };

  const handleToggleVisible = async (item: NavItem) => {
    try {
      const { error } = await supabase
        .from('nav_items')
        .update({ is_visible: !item.is_visible })
        .eq('id', item.id);

      if (error) throw error;
      await loadNavItems();
    } catch (err) {
      
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    await swapOrder(navItems[index], navItems[index - 1]);
  };

  const handleMoveDown = async (index: number) => {
    if (index === navItems.length - 1) return;
    await swapOrder(navItems[index], navItems[index + 1]);
  };

  const swapOrder = async (item1: NavItem, item2: NavItem) => {
    try {
      await supabase
        .from('nav_items')
        .update({ sort_order: item2.sort_order })
        .eq('id', item1.id);

      await supabase
        .from('nav_items')
        .update({ sort_order: item1.sort_order })
        .eq('id', item2.id);

      await loadNavItems();
    } catch (err) {
      
    }
  };

  const openModal = (item?: NavItem) => {
    if (item) {
      setEditingItem(item);
      setForm({
        name: item.name,
        path: item.path,
        icon: item.icon || '',
        link_type: item.link_type,
        is_visible: item.is_visible,
      });
    } else {
      setEditingItem(null);
      setForm({
        name: '',
        path: '',
        icon: '',
        link_type: 'internal',
        is_visible: true,
      });
    }
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setError('');
  };

  const getLinkTypeLabel = (type: string) => {
    return linkTypeOptions.find(opt => opt.value === type)?.label || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">导航栏管理</h1>
            <p className="text-gray-500 mt-1">管理网站顶部导航栏的显示、排序和链接</p>
          </div>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            添加导航项
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LayoutGrid className="w-4 h-4" />
              <span>共 {navItems.length} 个导航项</span>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {navItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors ${
                  !item.is_visible ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === navItems.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>

                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
                  {index + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{item.name}</span>
                    {item.is_system && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                        系统
                      </span>
                    )}
                    {item.is_custom && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">
                        自定义
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                    <span className="truncate">{item.path}</span>
                    <span className="text-gray-300">|</span>
                    <span>{getLinkTypeLabel(item.link_type)}</span>
                    {item.icon && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span>图标: {item.icon}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleVisible(item)}
                    className={`p-2 rounded-lg transition-colors ${
                      item.is_visible
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={item.is_visible ? '点击隐藏' : '点击显示'}
                  >
                    {item.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>

                  <button
                    onClick={() => openModal(item)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="编辑"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  {!item.is_system && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="删除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}

            {navItems.length === 0 && (
              <div className="px-6 py-12 text-center text-gray-500">
                <Menu className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>暂无导航项</p>
                <button
                  onClick={() => openModal()}
                  className="mt-3 text-blue-600 hover:text-blue-700"
                >
                  添加第一个导航项
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-medium text-blue-900 mb-2">使用说明</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• 点击眼睛图标可切换导航项的显示/隐藏状态</li>
            <li>• 使用上下箭头调整导航项的排序顺序</li>
            <li>• 系统导航项不可删除，但可以隐藏</li>
            <li>• 修改后前端导航栏会实时更新</li>
          </ul>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingItem ? '编辑导航项' : '添加导航项'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    导航名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="如：首页、关于我们"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    链接路径 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.path}
                    onChange={e => setForm({ ...form, path: e.target.value })}
                    placeholder={form.link_type === 'external' ? 'https://example.com' : '/about'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {form.link_type === 'external' ? '请输入完整的URL地址' : '请输入以 / 开头的路径'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    链接类型
                  </label>
                  <select
                    value={form.link_type}
                    onChange={e => setForm({ ...form, link_type: e.target.value as 'internal' | 'external' | 'custom_page' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    {linkTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    图标
                  </label>
                  <select
                    value={form.icon}
                    onChange={e => setForm({ ...form, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    {iconOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_visible"
                    checked={form.is_visible}
                    onChange={e => setForm({ ...form, is_visible: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_visible" className="text-sm text-gray-700">
                    显示在导航栏
                  </label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  保存
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
